import { each, extend, filter } from 'lodash';
import moment from 'moment';

import Jed from '../lib/core/jed';
import de_DE from '../locales/de_DE';
import en_US from '../locales/en_US';
import es_ES from '../locales/es_ES';
import fr_FR from '../locales/fr_FR';
import it_IT from '../locales/it_IT';
import pt_PT from '../locales/pt_PT';
import { Locales, LocalesLong } from '../types/common';

// import all locales for moment needed by moment
// EN loaded by default
import 'moment/locale/fr';
import 'moment/locale/de';
import 'moment/locale/it';
import 'moment/locale/es';
import 'moment/locale/pt';

/*
 ** Functions
 */

const getLocaleFile = (locale) => {
  switch (locale) {
    case Locales.EN:
      return en_US;
    case Locales.DE:
      return de_DE;
    case Locales.IT:
      return it_IT;
    case Locales.ES:
      return es_ES;
    case Locales.PT:
      return pt_PT;
    case Locales.FR:
      return fr_FR;
    default:
      return fr_FR;
  }
};

const getSiteLanguage = () => {
  let siteLang = null;
  if (typeof window.config === 'object') {
    siteLang = window.config.LANGUAGE;
    if (typeof siteLang === 'string') {
      siteLang = siteLang.toLowerCase();
    }
  } else if (typeof window.getSiteLanguage === 'function') {
    siteLang = window.getSiteLanguage();
    if (typeof siteLang === 'string') {
      siteLang = siteLang.toLowerCase();
    }
  } else if (typeof window.getAppLanguage === 'function') {
    siteLang = window.getAppLanguage().toLowerCase();
  }
  return siteLang;
};

// Data est un object Key/label qui alimente le catalogue des labels de l'application pour une langue donnée
function getJedConfig(language, plural, data) {
  const result = {};
  if (!data) {
    data = getLocaleFile(language).translations;
  }

  // On ajoute à chaque catalogue, le block de configuration
  each(data, (v, catalog) => {
    result[catalog] = extend(v, {
      '': {
        domain: catalog,
        lang: language,
        plural_forms: plural || 'nplurals=2; plural=(n > 1);',
      },
    });
  });
  return result;
}

let currentLocale = null;
let jed = null;
let currency = '\u20ac';
let decimalSeparator = ',';
let thousandSeparator = ' ';

const initializeL10n = (language, plural, next, action) => {
  jed = new Jed({
    locale_data: getJedConfig(language, plural),
  });
};

export const changeLanguageL10n = (language) => {
  if (!language) {
    return;
  }
  if (currentLocale !== language) {
    currentLocale = language;
    const { l10n } = getLocaleFile(currentLocale);
    const plural = l10n.number.plural;
    moment.locale(currentLocale);
    moment.updateLocale(currentLocale, {
      months: l10n.date?.months?.full,
      monthsShort: l10n.date?.months?.abbr,
      weekdays: l10n.date?.days?.full,
      weekdaysShort: l10n.date?.days?.abbr,
    });
    currency = l10n.currency || '\u20ac';
    decimalSeparator = l10n.number.decimal.separator;
    thousandSeparator = l10n.number.group.separator;
    if (!jed) {
      initializeL10n(language, plural);
    } else {
      jed.options.locale_data = getJedConfig(language, plural);
    }
  }
};

/**
 * INIT
 */
changeLanguageL10n(getSiteLanguage() || Locales.FR);

/**
 * PUBLIC
 */

export const __ = (str, args, plural, catalog) => {
  let trad = null;

  if (!str || !jed) {
    return '';
  }

  trad = jed.translate(str);
  if (catalog) {
    trad = trad.onDomain(catalog);
  }

  if (!args && args !== 0) {
    args = [];
  }

  if (!(args instanceof Array)) {
    args = [args];
  }

  if (plural && args.length) {
    trad = trad.ifPlural(args[0], plural);
  }
  return trad.fetch.apply(trad, args);
};

export const __jsx = (str, args, plural, catalog) => {
  let trad = null;

  if (!str || !jed) {
    return '';
  }

  trad = jed.translate(str);

  if (catalog) {
    trad = trad.onDomain(catalog);
  }

  if (!args && args !== 0) {
    args = [];
  }

  if (!(args instanceof Array)) {
    args = [args];
  }

  if (plural && args.length) {
    console.error("You can't translate plural form of jsx");
  }

  let jsx = trad.fetch().split(/(%[1-9]\$s)/g);

  each(args, (arg, index) => {
    const elem = jsx.indexOf(`%${index + 1}$s`);

    if (elem !== -1) {
      jsx[elem] = arg;
    }
  });
  return filter(jsx);
};

export const getCurrency = () => currency;

export const getLang = () => currentLocale;
export const getLangLong = () => LocalesLong[currentLocale];

export const getDecimalSeparator = () => decimalSeparator;

export const getThousandSeparator = () => thousandSeparator;
